<template>
  <div class="page-container">
    <!-- Banner Section -->
    <section class="h-[70vh] relative bg-cover bg-center" :style="{ backgroundImage: `url(${banner_about})` }">
      <div class="relative z-10 h-full flex items-center justify-start text-white pl-[9%]">
        <div>
          <div class="flex items-center gap-x-6">
            <h2 class="font-bold text-[2.6vw] whitespace-nowrap">{{ aboutTexts.title }}</h2>
            <p class="font-normal text-[1.4vw] w-[33vw] font-alibaba leading-[1.6]">
              {{ aboutTexts.description }}
            </p>
          </div>

          <h1 class="font-bold text-[2.6vw] mt-1vw">{{ aboutTexts.company }}</h1>
        </div>
      </div>
    </section>

    <BusinessSegments />

    <!-- Mission / Vision / Values -->
    <section class="bg-[#F3F8FF]">
      <div class="w-[81%] mx-auto mt-[2.6vw] mb-[2.6vw]">
        <!-- Mission Section -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 items-center">
          <div class="relative h-48 md:h-64">
            <img :src="Mission" alt="Mission" class="w-full h-full object-cover" />
          </div>
          <div class="bg-white p-6 shadow-sm min-h-64 flex items-center">
            <p class="text-black text-[1.3vw] font-medium leading-[1.5]">
              {{ aboutTexts.mission }}
            </p>
          </div>
        </div>

        <!-- Vision Section -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 items-center">
          <div class="bg-white p-6 shadow-sm order-2 md:order-1 min-h-64 flex items-center">
            <p class="text-black text-[1.3vw] font-medium leading-[1.5]">
              {{ aboutTexts.vision }}
            </p>
          </div>
          <div class="relative h-48 md:h-64 order-1 md:order-2">
            <img :src="Vision" alt="Vision" class="w-full h-full object-cover" />
          </div>
        </div>

        <!-- Value Section -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div class="relative h-48 md:h-64">
            <img :src="Value" alt="Value" class="w-full h-full object-cover" />
          </div>
          <div class="bg-white p-6 shadow-sm min-h-64 flex items-center">
            <ul class="text-black text-[1.3vw] font-medium list-disc list-inside space-y-2 leading-[1.5]">
              <li v-for="(val, key) in valueItems" :key="key">{{ val }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Strengths -->
    <section class="w-full pb-3vw bg-[#0B0B7C]">
      <div class="py-5 text-white">
        <h2 class="text-2.6vw font-bold text-left w-[81%] mx-auto mt-3.0vw">
          {{ aboutTexts.strengths?.title }}
        </h2>
        <div class="w-full mx-auto mt-3.0vw">
          <div class="flex justify-between gap-x-4">
            <div v-for="col in 5" :key="col" :class="['flex flex-col gap-y-4 w-full', getColumnOffset(col)]">
              <div v-for="(item, index) in getColumnItems(col)" :key="index"
                class="relative w-full rounded-[2.6vw] overflow-hidden shadow-lg group" style="aspect-ratio: 372 / 570">
                <img :src="item.image" alt="Strength Image" class="absolute inset-0 w-full h-full object-cover" />

                <div v-if="item.label && item.subTitle && item.label.trim() && item.subTitle.trim()"
                  class="absolute inset-0 z-10 rounded-[2.6vw] flex flex-col items-center text-white opacity-100 pointer-events-auto"
                  :style="{ background: item.color || 'rgba(0,0,0,0.7)' }">
                  <div class="transform translate-y-0 opacity-100 flex flex-col items-center w-full">
                    <p class="text-[1.5vw] w-[88%] font-bold mt-[3vw] leading-[1.3]">
                      {{ item.label }}
                    </p>
                    <div class="w-[90%] h-[1px] bg-white my-[1vw] leading-[1.3]"></div>
                    <p class="text-[1.1vw] w-[90%] font-medium opacity-80 leading-[1.5]">
                      {{ item.subTitle }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Certification -->
    <section class="h-screen h-[95vh] flex flex-col items-center bg-no-repeat bg-cover text-white"
      :style="{ backgroundImage: `url(${about_Certification})` }">
      <div class="w-[80%] mx-auto text-left">
        <p class="text-left text-black font-bold text-[2.6vw] mt-3.0vw">
          {{ aboutTexts.certification }}
        </p>
      </div>
    </section>

    <!-- Gallery -->
    <section class="bg-[#F3F8FF]">
      <ImageGallery :imageItems="ourStyles" imageHeightClass="max-h-[80vh]" :imagesPerPage="1" columns="1" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import Mission from "../assets/Mission.png";
import Vision from "../assets/Vision.png";
import Value from "../assets/Value.png";
import BusinessSegments from "../components/BusinessSegments.vue";
import { useRouter } from "vue-router";
import about_Certification from "../assets/img_about_Certification.png";
import banner_about from "../assets/banner_about.jpg";
import { strengths } from "../utils/imageData";
import { pushToDataLayer } from "../utils/gtm";
import { useChatWidget } from "../utils/chatWidget";
import ImageGallery from "../components/ImageGallery.vue";
import { ourStyles } from "../utils/imageData";
import { useI18n } from 'vue-i18n'

const { locale, messages } = useI18n()

// 获取关于我们的文本
const aboutTexts = computed(() => {
  return messages.value[locale.value]?.about || {};
});

const valueItems = computed(() => aboutTexts.value.values?.items || [])

// 合并图片和国际化文本数据
const strengthsData = computed(() => {
  const textItems = aboutTexts.value.strengths?.items || []
  return strengths.map((strengthItem, index) => ({
    image: strengthItem.image,
    label: textItems[index]?.label || strengthItem.label || '',
    subTitle: textItems[index]?.subTitle || strengthItem.subTitle || '',
    color: textItems[index]?.color || strengthItem.color
  }))
})

const router = useRouter();
const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("AboutUs");
  pushToDataLayer("Chat_With_Us", {
    category: "AboutUs",
    label: "Chat With Us",
  });
};

const getColumnItems = (col) => {
  return strengthsData.value.filter((_, index) => index % 5 === col - 1);
};

const getColumnOffset = (col) => {
  if (col === 2 || col === 4) {
    return "mt-[3vw]";
  }
  return "";
};
</script>

<style scoped>
.page-container {
  width: 100%;
}
</style>
